import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'lodash'

import { TextInput, Dropdown, DatePicker, Loader } from 'components'
import { IconSearch, IconClose } from 'components/Icons'
import {
  useFindAllTaskType,
  filterOptionBuilder,
  withAll,
  PRIORITY_OPTIONS,
  TICKET_OPTIONS,
} from 'features/Ticket'
import { findSelected, parseDate, formatDate, onEnter } from 'helpers/common'

import { useSearchParams } from 'helpers/hooks'

import type { OptionProps } from 'types'
import type { IParamTicket } from 'features/Ticket'

interface propsTicketSearch {
  isLoading: boolean
  fetchData: (
    searchParams?: IParamTicket,
    option?: {
      isRemoveStatusProgress: boolean
    },
  ) => Promise<void>
}

function TicketSearchAndFilter(props: propsTicketSearch): JSX.Element {
  const { isLoading, fetchData } = props
  const [searchParams, setSearchParams] = useSearchParams<IParamTicket>({})
  const [{ data: taskType, loading: taskTypeLoading }] = useFindAllTaskType()

  const { q = '', statusPriority = '' } = searchParams
  const startDate = parseDate(searchParams['requestDate[gte]'])
  const endDate = parseDate(searchParams['requestDate[lte]'])

  const [search, setSearch] = useState<string>(q)
  const [firstLanding, setFirstLanding] = useState<boolean>(false)

  const [isCloseChipStatus, setIsCloseChipStatus] = useState<boolean>(false)
  const [listStatus, setListStatus] = useState<OptionProps[]>(TICKET_OPTIONS)

  const { t } = useTranslation(['common', 'ticket'])

  const base = 'f-ticket-header-con'

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    setSearch(e.target.value)
  }

  const handleSearchSubmit = () => {
    setSearchParams({ q: search.trim(), _page: 1 })
  }

  const handleClearSearch = () => {
    setSearch('')
    setSearchParams({ q: '', _page: 1 })
  }

  const handleDateChange = (
    key: keyof Pick<IParamTicket, 'requestDate[gte]' | 'requestDate[lte]'>,
    date: string,
  ) => {
    setSearchParams({ [key]: date, _page: 1 })
  }

  const handleDropdownChange = (
    key: keyof Omit<IParamTicket, 'requestDate[gte]' | 'requestDate[lte]'>,
    option: OptionProps,
  ) => {
    setSearchParams({ [key]: option.key, _page: 1 })
  }

  const statusTypeAll = listStatus[listStatus.length - 1]
  const filterSelect = listStatus.filter(({ isSelect }) => isSelect)
  const filterSelectKey = filterSelect.map((e) => {
    return e.key
  })

  const selectStatus = (typeKey: string) => {
    const index = listStatus.findIndex((e) => e.key === typeKey)

    if (listStatus[index].key === statusTypeAll.key) {
      // In case select status all task
      setListStatus(
        listStatus.map((e) => {
          return {
            ...e,
            isSelect: !statusTypeAll.isSelect,
          }
        }),
      )
    } else {
      listStatus[index].isSelect = !listStatus[index].isSelect
      const checkSelectAllList = listStatus.filter(
        (e) => e.isSelect && e.key !== statusTypeAll.key,
      )
      statusTypeAll.isSelect =
        checkSelectAllList.length === TICKET_OPTIONS.length - 1
      setListStatus([...listStatus])
    }
  }

  useEffect(() => {
    let defaultListStatus = TICKET_OPTIONS
    const [
      { key: STATUS_NEW },
      { key: STATUS_IN_PROGRESS },
      { key: STATUS_PENDING },
      { key: STATUS_TRANSFERRING },
      { key: STATUS_REJECTED },
    ] = TICKET_OPTIONS

    if (isEmpty(searchParams.statusProgress)) {
      // Set default select status 5 list when user first come to page
      const defaultSearch = [
        STATUS_NEW,
        STATUS_IN_PROGRESS,
        STATUS_PENDING,
        STATUS_TRANSFERRING,
        STATUS_REJECTED,
      ]
      setSearchParams({
        statusProgress: defaultSearch,
        _page: 1,
      })
      defaultListStatus = TICKET_OPTIONS.map((e) => {
        return {
          ...e,
          isSelect: Boolean(defaultSearch.includes(e.key)),
        }
      })
    } else if (searchParams?.statusProgress) {
      // Set select status depend query path url when user has select status
      defaultListStatus = TICKET_OPTIONS.map((e) => {
        return {
          ...e,
          isSelect: Array.isArray(searchParams?.statusProgress)
            ? Boolean(
                searchParams?.statusProgress.find((name) => name === e.key),
              )
            : searchParams?.statusProgress === e.key,
        }
      })
    } else if (searchParams['_page']) {
      // Set select status all list when user select when user has select status
      defaultListStatus = TICKET_OPTIONS.map((e) => {
        return {
          ...e,
          isSelect: true,
        }
      })
    }

    setListStatus(defaultListStatus)
    setFirstLanding(true)
  }, [])

  useEffect(() => {
    if (isCloseChipStatus) {
      setIsCloseChipStatus(false)
      fetchData({
        statusProgress: filterSelectKey,
        _page: 1,
      })
    }
  }, [listStatus, isCloseChipStatus])

  const handleDropdownStatus = () => {
    if (!Boolean(firstLanding)) {
      return
    }

    const newOption = filterSelectKey.filter((item) => item !== '')

    if (!isEmpty(newOption)) {
      fetchData({
        ...searchParams,
        statusProgress: newOption,
        _page: 1,
      })
    } else {
      fetchData(searchParams, { isRemoveStatusProgress: true })
    }
  }

  const filterSelectStatus = () => {
    if (statusTypeAll.isSelect || filterSelect.length === 0) {
      return statusTypeAll
    } else {
      return filterSelect[0]
    }
  }

  const countCheckbox = () => {
    const checkSelectAllList = listStatus.filter(
      (e) => e.isSelect && e.key !== statusTypeAll.key,
    )
    return checkSelectAllList.length - 1
  }

  return (
    <div className={base}>
      <TextInput
        showIcon={isLoading}
        isLoading={isLoading}
        disabled={isLoading}
        placeholder={t('ticket:searchList.placeholder')}
        name='search'
        onChange={handleSearchChange}
        value={search}
        variants='secondary'
        onKeyDown={onEnter(handleSearchSubmit)}
        withIcon={
          q && q === search ? (
            <span
              data-testid='close-ticket'
              className={`${base}__in-line-btn`}
              onClick={handleClearSearch}
            >
              <IconClose variants='sm' />
            </span>
          ) : (
            <span
              data-testid='search-ticket'
              className={`${base}__in-line-btn`}
              onClick={handleSearchSubmit}
            >
              <IconSearch variants='sm' />
            </span>
          )
        }
      />
      <Dropdown
        keyTest='status-priority'
        className={`${base}__dropdown`}
        options={PRIORITY_OPTIONS}
        selected={findSelected(PRIORITY_OPTIONS, statusPriority)}
        onChange={(option) => handleDropdownChange('statusPriority', option)}
        label='common:priority.placeholder'
        variants='secondary'
      />

      <div className={`${base}__second-row`}>
        <DatePicker
          value={startDate}
          onChange={(date) =>
            handleDateChange('requestDate[gte]', formatDate(date))
          }
          disabledDate={(date) => Boolean(endDate && date >= endDate)}
          className={`${base}__date-picker`}
          variants='secondary'
          placeholder={t('common:startDate.placeholder')}
          withIcon={
            startDate && (
              <span
                data-testid='start-date-icon'
                className={`${base}__in-line-btn`}
                onClick={() => {
                  handleDateChange('requestDate[gte]', '')
                }}
              >
                <IconClose variants='sm' />
              </span>
            )
          }
        />
        <DatePicker
          value={endDate}
          onChange={(date) =>
            handleDateChange('requestDate[lte]', formatDate(date))
          }
          disabledDate={(date) => Boolean(startDate && date <= startDate)}
          className={`${base}__date-picker`}
          variants='secondary'
          placeholder={t('common:endDate.placeholder')}
          withIcon={
            endDate && (
              <span
                data-testid='end-date-icon'
                className={`${base}__in-line-btn`}
                onClick={() => {
                  handleDateChange('requestDate[lte]', '')
                }}
              >
                <IconClose variants='sm' />
              </span>
            )
          }
        />

        <Dropdown
          className={`${base}__dropdown`}
          options={listStatus}
          selected={filterSelectStatus()}
          label='ticket:status.placeholder'
          variants='secondary'
          isCheckbox
          countCheckbox={countCheckbox()}
          selectCheckbox={selectStatus}
          displaySelect={filterSelect.length > 0}
          onFilterCheckBox={handleDropdownStatus}
          onCloseChip={setIsCloseChipStatus}
        />

        <Dropdown
          keyTest='task-type'
          className={`${base}__dropdown`}
          label='ticket:type.placeholder'
          withIcon={taskTypeLoading && <Loader />}
          options={withAll(filterOptionBuilder(taskType))}
          selected={findSelected(
            withAll(filterOptionBuilder(taskType)),
            searchParams['type.id'] ? Number(searchParams['type.id']) : '',
          )}
          onChange={(option) => handleDropdownChange('type.id', option)}
          variants='secondary'
        />
      </div>
    </div>
  )
}

export default TicketSearchAndFilter
